import { useState, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseEther } from 'viem';
import { message } from 'antd';
import { api } from '@/api/api';
import { CONTRACTS, ABIS } from '@/contracts/config';
import { useNctBalance } from './useNctBalance';
import {
  Club,
  PlayerClubStatus,
  ClubRankingEntry,
  TransferInfo,
  ClubListResponse,
  PlayerStatusResponse,
  ClubRankingResponse,
  TransferCostResponse,
  KOL
} from '@/types/club';

export interface ClubState {
  clubs: Club[];
  playerStatus: PlayerClubStatus | null;
  rankings: ClubRankingEntry[];
  loading: boolean;
  error: string | null;
  transferLoading: boolean;
  transferCost: number;
  // 转会交易相关状态
  isTransferring: boolean;
  transferTxHash: string | null;
  isProcessingTransfer: boolean;
  selectedTransferClub: number | null;
}

export const useClub = () => {
  const [state, setState] = useState<ClubState>({
    clubs: [],
    playerStatus: null,
    rankings: [],
    loading: true,
    error: null,
    transferLoading: false,
    transferCost: 0,
    isTransferring: false,
    transferTxHash: null,
    isProcessingTransfer: false,
    selectedTransferClub: null,
  });

  const { address } = useAccount();
  
  // 使用统一的NCT余额和授权hook
  const { 
    balance: nctBalance, 
    checkAllowance, 
    approve: approveNct,
    isApproving,
    approveHash,
    isApproveConfirming,
    refetchAllowance,
    refetch: refetchBalance,
    reset: resetNctState,
  } = useNctBalance();

  // 转会合约调用
  const {
    writeContract: transferContract,
    data: transferHash,
    error: transferError,
  } = useWriteContract();

  // 等待转会交易确认
  const { 
    isLoading: isTransferConfirming,
    data: transferReceipt 
  } = useWaitForTransactionReceipt({
    hash: transferHash,
    query: {
      enabled: !!transferHash,
      refetchInterval: 500,
    },
  });

  // 获取俱乐部列表
  const fetchClubs = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const response = await api.get<ClubListResponse>('/cryptokill/club/list');
      const data = response.data;
      
      if (data.success && data.clubs) {
        setState(prev => ({ ...prev, clubs: data.clubs, loading: false, error: null }));
      } else {
        console.error('获取俱乐部列表失败:', data.message);
        setState(prev => ({ 
          ...prev, 
          clubs: [], 
          loading: false, 
          error: data.message || '获取俱乐部数据失败' 
        }));
      }
    } catch (error) {
      console.error('获取俱乐部列表失败:', error);
      setState(prev => ({ 
        ...prev, 
        clubs: [], 
        loading: false, 
        error: '网络连接失败' 
      }));
    }
  };

  // 获取玩家俱乐部状态
  const fetchPlayerStatus = async (wallet: string) => {
    try {
      const response = await api.get<PlayerStatusResponse>(`/cryptokill/club/player-status?wallet=${wallet}`);
      const data = response.data;
      
      if (data.success) {
        setState(prev => ({ ...prev, playerStatus: data.status, error: null }));
      } else {
        console.error('获取玩家状态失败:', data.message);
        setState(prev => ({ 
          ...prev, 
          playerStatus: null,
          error: data.message || '获取玩家状态失败'
        }));
      }
    } catch (error) {
      console.error('获取玩家状态失败:', error);
      setState(prev => ({ 
        ...prev, 
        playerStatus: null,
        error: '网络连接失败'
      }));
    }
  };

  // 获取俱乐部排行榜
  const fetchRankings = async () => {
    try {
      const response = await api.get<ClubRankingResponse>('/cryptokill/club/rankings');
      const data = response.data;
      
      if (data.success && data.rankings) {
        setState(prev => ({ ...prev, rankings: data.rankings, error: null }));
      } else {
        console.error('获取排行榜失败:', data.message);
        setState(prev => ({ 
          ...prev, 
          rankings: [], 
          error: data.message || '获取排行榜失败' 
        }));
      }
    } catch (error) {
      console.error('获取排行榜失败:', error);
      setState(prev => ({ 
        ...prev, 
        rankings: [], 
        error: '网络连接失败' 
      }));
    }
  };

  // 加入俱乐部
  const joinClub = async (clubId: number) => {
    if (!address) {
      message.error('请先连接钱包');
      return false;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      const response = await api.post('/cryptokill/club/join', { 
        clubId, 
        walletAddress: address 
      });
      const data = response.data;
      
      if (data.success) {
        // 重新获取玩家状态和俱乐部数据
        await Promise.all([
          fetchPlayerStatus(address),
          fetchClubs(),
          fetchRankings()
        ]);
        
        const targetClub = state.clubs.find(club => club.id === clubId);
        message.success(`成功加入 ${targetClub?.name || '俱乐部'}！`);
        setState(prev => ({ ...prev, loading: false, error: null }));
        return true;
      } else {
        console.error('加入俱乐部失败:', data.message);
        message.error(data.message || '加入俱乐部失败');
        setState(prev => ({ ...prev, loading: false, error: data.message }));
        return false;
      }
    } catch (error) {
      console.error('加入俱乐部失败:', error);
      message.error('网络连接失败，请稍后重试');
      setState(prev => ({ ...prev, loading: false, error: '网络连接失败' }));
      return false;
    }
  };

  // 计算转会费用
  const calculateTransferCost = async (fromClubId: number | undefined, toClubId: number) => {
    if (!address) {
      setState(prev => ({ ...prev, transferCost: 1000 }));
      return 1000;
    }

    try {
      const response = await api.get<TransferCostResponse>(
        `/cryptokill/club/transfer-cost?from=${fromClubId || ''}&to=${toClubId}&wallet=${address}`
      );
      const data = response.data;
      
      if (data.success) {
        // 使用后端计算的费用，不强制最低限制
        setState(prev => ({ ...prev, transferCost: data.cost, error: null }));
        return data.cost;
      } else {
        console.error('计算转会费用失败:', data.message);
        setState(prev => ({ 
          ...prev, 
          transferCost: 1000, 
          error: data.message || '计算转会费用失败' 
        }));
        return 1000;
      }
    } catch (error) {
      console.error('计算转会费用失败:', error);
      setState(prev => ({ 
        ...prev, 
        transferCost: 1000, 
        error: '网络连接失败' 
      }));
      return 1000;
    }
  };

  // 验证转会费用
  const validateTransferCost = (cost: number): { isValid: boolean; error: string | null } => {
    if (cost <= 0) {
      return { isValid: false, error: '转会费用无效' };
    }
    
    const currentBalance = parseFloat(nctBalance || '0');
    if (cost > currentBalance) {
      return { isValid: false, error: `NClab余额不足，需要 ${cost.toLocaleString()} NClab，当前余额 ${currentBalance.toLocaleString()} NClab` };
    }
    
    return { isValid: true, error: null };
  };

  // 检查是否需要授权
  const needsApprovalForTransfer = (amount: string): boolean => {
    return !checkAllowance(CONTRACTS.PurchaseManager || '', amount);
  };

  // 转会
  const transferClub = async (toClubId: number) => {
    if (!address || !state.playerStatus?.clubId) {
      message.error('转会条件不满足');
      return false;
    }

    // 设置选中的转会俱乐部
    setState(prev => ({ ...prev, selectedTransferClub: toClubId }));

    // 验证转会费用
    const validation = validateTransferCost(state.transferCost);
    if (!validation.isValid) {
      message.error(validation.error || '转会费用验证失败');
      return false;
    }

    const transferAmount = state.transferCost.toString();

    // 检查是否需要授权
    if (needsApprovalForTransfer(transferAmount)) {
      // 需要授权 - 执行授权操作
      setState(prev => ({ ...prev, transferLoading: true, error: null }));
      
      try {
        const approved = await approveNct(CONTRACTS.PurchaseManager || '', transferAmount);
        
        setState(prev => ({ ...prev, transferLoading: false }));
        
        if (!approved) {
          setState(prev => ({ ...prev, error: '授权失败' }));
          message.error('授权失败，无法进行转会');
          return false;
        }
        
        // 授权交易已提交，等待确认
        message.info('授权交易已提交，请等待确认');
        return false; // 返回false，等待授权完成后用户再次点击
        
      } catch (error: any) {
        setState(prev => ({ ...prev, transferLoading: false, error: '授权失败' }));
        message.error('授权失败');
        return false;
      }
    }

    // 已授权，执行转会交易
    setState(prev => ({ 
      ...prev, 
      transferLoading: true, 
      isTransferring: true,
      error: null,
      isProcessingTransfer: true
    }));

    try {
      const amountWei = parseEther(transferAmount);

      // 提交转账交易到PurchaseManager合约
      transferContract({
        address: CONTRACTS.PurchaseManager as `0x${string}`,
        abi: (ABIS.PURCHASE_MANAGER as any).abi,
        functionName: "deposit" as const,
        args: [amountWei],
      });

      return true; // 立即返回，等待交易确认

    } catch (error: any) {
      let errorMessage = '转会失败';
      if (error.message?.includes('User rejected')) {
        errorMessage = '用户取消交易';
      } else if (error.message?.includes('insufficient funds')) {
        errorMessage = '余额不足';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
      setState(prev => ({ 
        ...prev, 
        transferLoading: false,
        isTransferring: false,
        isProcessingTransfer: false,
        error: errorMessage
      }));
      
      return false;
    }
  };

  // 初始化数据
  useEffect(() => {
    const initData = async () => {
      // 清理可能存在的旧授权状态
      resetNctState();
      
      await Promise.all([
        fetchClubs(),
        fetchRankings()
      ]);
    };

    initData();
  }, []); // 只在组件挂载时执行一次

  // 当钱包连接时获取玩家状态
  useEffect(() => {
    if (address) {
      fetchPlayerStatus(address);
    } else {
      setState(prev => ({ ...prev, playerStatus: null }));
      // 钱包断开时也重置授权状态
      resetNctState();
    }
  }, [address]); // 移除 resetNctState 依赖

  // 监听授权交易状态变化 - 参考bridge的做法
  useEffect(() => {
    if (approveHash && !isApproveConfirming) {
      setTimeout(() => {
        refetchAllowance(CONTRACTS.PurchaseManager || '');
        refetchBalance();
        message.success('授权完成，现在可以进行转会了');
      }, 500);
    }
  }, [approveHash, isApproveConfirming]); // 移除 refetchAllowance, refetchBalance 依赖

  // 监听转会交易确认并处理后端逻辑
  useEffect(() => {
    const handleTransferConfirmation = async () => {
      // 确保有交易hash、交易已确认、不在确认中、且交易进行中
      if (!transferHash || !transferReceipt || isTransferConfirming || !state.isProcessingTransfer) {
        return;
      }

      try {
        if (transferReceipt.status === 'success') {
          message.success('转会交易确认成功！');
          
          setState(prev => ({ 
            ...prev, 
            transferTxHash: transferHash,
          }));

          // 调用后端API完成转会
          try {
            const response = await api.post('/cryptokill/club/transfer', {
              fromClubId: state.playerStatus?.clubId,
              toClubId: state.selectedTransferClub,
              walletAddress: address,
              txHash: transferHash,
              amount: state.transferCost.toString(),
            });
            const data = response.data;

            if (data.success) {
              // 重新获取玩家状态和俱乐部数据
              await Promise.all([
                fetchPlayerStatus(address!),
                fetchClubs(),
                fetchRankings()
              ]);

              const targetClub = state.clubs.find(club => club.id === state.selectedTransferClub);
              message.success(`成功转会至 ${targetClub?.name || '俱乐部'}！`);
              
              setState(prev => ({
                ...prev,
                transferLoading: false,
                isTransferring: false,
                isProcessingTransfer: false,
                error: null,
                selectedTransferClub: null,
              }));
              
            } else {
              throw new Error(data.message || '后端处理失败');
            }
            
          } catch (backendError) {
            console.error('后端处理失败:', backendError);
            setState(prev => ({
              ...prev,
              transferLoading: false,
              isTransferring: false,
              isProcessingTransfer: false,
              error: '转会交易成功，但后端处理失败，请联系客服'
            }));
            message.error('转会交易成功，但后端处理失败，请联系客服');
          }
          
        } else {
          // 交易失败
          message.error('区块链交易失败');
          setState(prev => ({ 
            ...prev, 
            transferLoading: false,
            isTransferring: false,
            isProcessingTransfer: false,
            error: '区块链交易失败'
          }));
        }
        
      } catch (error) {
        console.error('处理交易确认时出错:', error);
        setState(prev => ({ 
          ...prev, 
          transferLoading: false,
          isTransferring: false,
          isProcessingTransfer: false,
          error: '处理交易确认时出错'
        }));
      }
    };

    handleTransferConfirmation();
  }, [transferHash, transferReceipt, isTransferConfirming, state.isProcessingTransfer, state.selectedTransferClub, address, state.playerStatus?.clubId, state.transferCost, state.clubs]);

  // 监听转会交易错误
  useEffect(() => {
    if (transferError && state.isTransferring) {
      message.error('转会交易失败');
      setState(prev => ({ 
        ...prev, 
        transferLoading: false,
        isTransferring: false,
        isProcessingTransfer: false,
        error: '转会交易失败'
      }));
    }
  }, [transferError, state.isTransferring]);

  return {
    state,
    nctBalance,
    isApproving,
    actions: {
      joinClub,
      transferClub,
      calculateTransferCost,
      validateTransferCost,
      needsApprovalForTransfer,
      resetNctState,
      fetchClubs,
      fetchPlayerStatus,
      fetchRankings,
    },
  };
}; 